import { inject } from "@angular/core";
import { CanActivateFn, Router, Routes, UrlTree } from "@angular/router";
import { MsalGuard } from "@azure/msal-angular";
import { UserService } from "./app/core/auth/user.service";
import { hasPartnersGuard } from "./app/has-partners.guard";
import { isNotRegisteredGuard } from "./app/is-not-registered.guard";
import { redirectGuard } from "./app/redirect.guard";

const defaultRouteGuard: CanActivateFn = async (): Promise<UrlTree> => {
  const router = inject(Router);
  const user = inject(UserService);
  await user.isLoaded;

  return router.parseUrl("/partners/_/sites/_");
};

export const APP_ROUTES: Routes = [
  {
    path: "login",
    loadComponent: () =>
      import("./app/login.component").then((c) => c.LoginComponent),
  },
  // **Note regarding route guards:**
  //
  // `MsalGuard` currently seems to have a bug where it won't work as a
  // `canLoad` guard in our current scenario, so we have to use `canActivate`
  // even when loading a separate module.
  //
  // Likewise, in routes using `MsalGuard` for all other guards that wait for
  // the user to be loaded, we have to use `canActivate` or else they will lock
  // up waiting for the load, apparently preventing MSAL from redirecting to the
  // login page.
  //
  // See: https://github.com/angular/angular/issues/27826#issuecomment-451304062
  //
  // If `MsalGuard` is ever fixed to support `canLoad` properly, the other
  // guards can also likely be switched to `canLoad` where appropriate.
  {
    path: "",
    pathMatch: "full",
    canActivate: [MsalGuard, defaultRouteGuard],
    loadComponent: () =>
      import("./app/core/elements/loading.component").then(
        (c) => c.LoadingComponent,
      ),
  },
  {
    path: "partners",
    canActivate: [MsalGuard, hasPartnersGuard],
    loadChildren: () =>
      import("./app/partner/partner.routes").then((r) => r.PARTNER_ROUTES),
  },
  {
    path: "redirect",
    canActivate: [MsalGuard, redirectGuard],
    // Arbitrary component. It shouldn't ever be rendered as the
    // redirectGuard should always redirect away regardless.
    loadComponent: () =>
      import("./app/core/elements/loading.component").then(
        (c) => c.LoadingComponent,
      ),
  },
  {
    path: "account-pending",
    canActivate: [MsalGuard],
    loadComponent: () =>
      import("./app/account-pending.component").then(
        (c) => c.AccountPendingComponent,
      ),
  },
  {
    path: "register",
    canActivate: [MsalGuard, isNotRegisteredGuard],
    loadComponent: () =>
      import("./app/registration-form.component").then(
        (c) => c.RegistrationFormComponent,
      ),
  },
  // Send everything else through the default route guard.
  { path: "**", redirectTo: "" },
];
